import {useCallback, useEffect, useMemo} from 'react'
import {useSearchParams} from 'react-router-dom'
import {ConversationsModal} from '../conversations-modal/ConversationsModal'
import {useInternalConversationsStore} from '@/features/conversations/store/useInternalConversationsStore'
import {usePermissions} from '@/features/auth/store/store'

export const INTERNAL_CONVERSATION_ID_PARAM = 'internal_conversation_id'

/**
 * Globally-mounted host (see PrivateLayout) for the internal conversations drawer:
 * renders the ConversationsModal without a patient (patientId null), driven by
 * useInternalConversationsStore and by the `internal_conversation_id` query param,
 * so the drawer can be opened (and deep-linked) from any authenticated route.
 */
export const InternalConversationsProvider = () => {
    const permissions = usePermissions()
    const isOpen = useInternalConversationsStore(state => state.isOpen)
    const initialConversationId = useInternalConversationsStore(state => state.initialConversationId)
    const open = useInternalConversationsStore(state => state.open)
    const close = useInternalConversationsStore(state => state.close)
    const clearInitialConversation = useInternalConversationsStore(state => state.clearInitialConversation)
    const [searchParams, setSearchParams] = useSearchParams()

    const paramConversationId = searchParams.get(INTERNAL_CONVERSATION_ID_PARAM) ?? null

    const clearParam = useCallback(() => {
        if (paramConversationId) {
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.delete(INTERNAL_CONVERSATION_ID_PARAM)
            setSearchParams(newSearchParams, {replace: true})
        }
    }, [paramConversationId, searchParams, setSearchParams])

    // Hosting the notification work-context bar. `closeSurface` deliberately skips clearParam:
    // when the bar ends the context it clears this param in the same navigation, and a second
    // write here would restore it — reopening the drawer the operator just left.
    const notificationContextHost = useMemo(
        () => ({searchParam: INTERNAL_CONVERSATION_ID_PARAM, closeSurface: close}),
        [close]
    )

    // Deep link: opening any authenticated route with ?internal_conversation_id=<id>
    // opens the drawer focused on that conversation (mirrors ConversationToggle's
    // handling of ?conversation_id= on patient pages)
    useEffect(() => {
        if (paramConversationId && !isOpen) {
            open(paramConversationId)
        }
    }, [paramConversationId, isOpen, open])

    const handleClose = useCallback(() => {
        close()
        clearParam()
    }, [close, clearParam])

    const handleClearInitialConversation = useCallback(() => {
        clearInitialConversation()
        clearParam()
    }, [clearInitialConversation, clearParam])

    if (!permissions?.conversations?.read) return null

    return (
        <ConversationsModal
            isOpen={isOpen}
            onClose={handleClose}
            patientId={null}
            initialConversationId={initialConversationId}
            onClearInitialConversation={handleClearInitialConversation}
            notificationContextHost={notificationContextHost}
        />
    )
}
